import { FC, useState } from 'react';

import {
    DO_NOT_HAVE_PERMISSIONS_MESSAGE,
    IPublicAccount,
    accountCanCreateSpark as accountCanCreateSparkFn,
} from '@sparkplug/lib';

import Button, { ButtonVariant } from '@components/buttons/Button';
import { Add as AddIcon, Lock as LockIcon } from '@components/icons';
import UpgradeModal from '@components/overlays/UpgradeModal';

import { useApp } from '@hooks/AppHooks';
import { useSparkplugAccount } from '@hooks/SparkplugAccountsHooks';

import './CreateSparkButton.scss';

interface CreateSparkButtonProps {
    className?: string;
    variant?: ButtonVariant;
    onClick?: () => void;
}

const useSparkCreateButtonState = ({
    onClick: handleClick,
}: Pick<CreateSparkButtonProps, 'onClick'>) => {
    const { account } = useSparkplugAccount();
    const { history, userIsSuperAdmin } = useApp();
    const [upgradeModalIsVisible, setUpgradeModalIsVisible] = useState(false);

    const defaultCreateSparkAction = () => {
        history.push(`/${account?._id}/sparks/create`);
    };

    const handleAccountCannotCreateSparks = () => setUpgradeModalIsVisible(true);

    const accountCanCreateSpark = accountCanCreateSparkFn(account ?? ({} as IPublicAccount));
    const startIcon = accountCanCreateSpark ? <AddIcon /> : <LockIcon />;
    const onClick: () => void =
        userIsSuperAdmin || accountCanCreateSpark
            ? handleClick ?? defaultCreateSparkAction
            : handleAccountCannotCreateSparks;

    const onCloseUpgradeModal = () => setUpgradeModalIsVisible(false);

    return {
        upgradeModalIsVisible,
        onCloseUpgradeModal,
        startIcon,
        onClick,
    };
};

const CreateSparkButton: FC<CreateSparkButtonProps> = ({
    variant = 'filled',
    onClick: handleClick,
    className = '',
}) => {
    const { startIcon, onClick, upgradeModalIsVisible, onCloseUpgradeModal } =
        useSparkCreateButtonState({
            onClick: handleClick,
        });
    const { userCan } = useSparkplugAccount();

    const userCanCreateSpark = userCan('createSpark');

    return (
        <>
            <Button
                className={`create-spark-btn ${className}`.trim()}
                color="blue"
                variant={variant}
                startIcon={startIcon}
                onClick={onClick}
                disabled={!userCanCreateSpark}
                tooltip={!userCanCreateSpark ? DO_NOT_HAVE_PERMISSIONS_MESSAGE : ''}
            >
                Create New Spark
            </Button>

            <UpgradeModal isVisible={upgradeModalIsVisible} onClose={onCloseUpgradeModal} />
        </>
    );
};

export default CreateSparkButton;
